import type { ComponentProps, ReactNode } from 'react'
import {
  FieldInfo,
  hasFieldError,
  readOnlyTriggerClass,
  useFieldContext,
  useFormReadOnly,
  useIsFieldOptional,
} from './shared'
import { Combobox } from '../combobox'
import { FieldLabelRow } from './field-label-row'
import { cn } from '@/lib/utils'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

export default function ComboboxTextField(
  props: ComponentProps<'select'> & {
    label: string
    search: string
    empty: string
    placeholder?: string
    options: { value: string; label: string }[]
    creatable?: boolean
    createLabel?: (value: string) => string
    onCreateNew?: (value: string) => void
    variant?: 'default' | 'outline'
    children?: ReactNode
    orientation?: 'vertical' | 'horizontal'
    optional?: boolean
  },
) {
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly()
  const isOptional = useIsFieldOptional(field.name, props.optional)
  const isHorizontal = props.orientation === 'horizontal'

  return (
    <div className={cn(props.className, formReadOnly && readOnlyTriggerClass)}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {props.label && (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          <Combobox
            id={field.name}
            placeholder={formReadOnly ? '-' : props.placeholder}
            commandEmpty={props.empty}
            searchPlaceholder={props.search}
            creatable={!formReadOnly && props.creatable}
            createLabel={props.createLabel}
            onCreateNew={props.onCreateNew}
            variant={props.variant}
            options={props.options}
            value={field.state.value}
            onChange={field.handleChange}
            isError={hasFieldError(field.state.meta)}
            children={props.children}
          />
          <FieldInfo fieldState={field.state} />
        </div>
      </div>
    </div>
  )
}
